import React from "react";

/** ===== Types ===== */
type Row = {
  symbol: string;
  last: number | null;
  last_ts: string | null;
  bid: number | null;
  ask: number | null;
  spread_bps: number | null;
  change_abs: number | null;
  change_pct: number | null;
  prev_close: number | null;
  day_low: number | null;
  day_high: number | null;
  w52_low: number | null;
  w52_high: number | null;
  day_volume: number | null;
  secs_since_trade: number | null;
  secs_since_quote: number | null;
  updated_at: string | null;
};

type SortKey =
  | "symbol"
  | "last"
  | "change_abs"
  | "change_pct"
  | "day_volume"
  | "bid"
  | "ask"
  | "last_ts";

type SortDir = "asc" | "desc";

/** ===== Formatters ===== */
// Prices: show 4 decimals to mimic Schwab rows
const fmtPx4 = (n: number | null | undefined) =>
  n == null ? "—" : `$${n.toFixed(4)}`;

// For low/high captions on range bars, 2 decimals reads cleaner
const fmtPx2 = (n: number | null | undefined) =>
  n == null ? "—" : `$${n.toFixed(2)}`;

const fmtPct = (n: number | null | undefined) =>
  n == null ? "—" : `${n.toFixed(2)}%`;

const fmtInt = (n: number | null | undefined) =>
  n == null ? "—" : n.toLocaleString("en-US");

const fmtET = (iso: string | null) => {
  if (!iso) return "—";
  const d = new Date(iso);
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(d);
  } catch {
    return "—";
  }
};

/** ===== Range bar =====
 * Shows [low ───────── high] with a marker at 'val'.
 * Gracefully handles nulls & zero-width ranges.
 */
function RangeBar({
  low,
  high,
  val,
  height = 8,
  title,
}: {
  low: number | null;
  high: number | null;
  val: number | null;
  height?: number;
  title?: string;
}) {
  const ok = low != null && high != null && low <= high;
  const span = ok ? Math.max(1e-9, high! - low!) : 1;
  let pct = 0.5;
  if (ok && val != null) pct = Math.min(1, Math.max(0, (val - low!) / span));

  const track = {
    background: "var(--soft, #263441)",
    border: "1px solid var(--border, #203040)",
    height,
    borderRadius: 999,
    position: "relative" as const,
  };
  const knob = {
    position: "absolute" as const,
    top: -2,
    left: `calc(${(pct * 100).toFixed(3)}% - 5px)`,
    width: 10,
    height: height + 4,
    borderRadius: 4,
    background: "var(--text, #e7edf3)",
    boxShadow: "0 0 0 1px var(--border, #203040)",
  };
  return (
    <div style={{ minWidth: 200 }}>
      <div style={track} title={title || ""}>
        <div style={knob} />
      </div>
      <div style={{ fontSize: 12, display: "flex", justifyContent: "space-between", opacity: 0.8 }}>
        <span>{fmtPx2(low)}</span>
        <span>{fmtPx2(high)}</span>
      </div>
    </div>
  );
}

/** ===== Main page ===== */
export default function WatchlistPage() {
  const [rows, setRows] = React.useState<Row[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [sort, setSort] = React.useState<SortKey>("symbol");
  const [dir, setDir] = React.useState<SortDir>("asc");
  const [refreshing, setRefreshing] = React.useState(false);
  const [auto, setAuto] = React.useState(true);

  const load = React.useCallback(async () => {
    try {
      setRefreshing(true);
      const u = new URL("/api/watchlist/live", window.location.origin);
      u.searchParams.set("sort", sort);
      u.searchParams.set("direction", dir);
      const r = await fetch(u.toString(), { cache: "no-store" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = (await r.json()) as Row[];
      setRows(j);
      setError(null);
    } catch (e: any) {
      setError(String(e?.message || e));
    } finally {
      setRefreshing(false);
    }
  }, [sort, dir]);

  React.useEffect(() => {
    load();
  }, [load]);

  React.useEffect(() => {
    if (!auto) return;
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, [auto, load]);

  const onSort = (k: SortKey) => {
    if (k === sort) {
      setDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSort(k);
      setDir(k === "symbol" ? "asc" : "desc");
    }
  };

  const sortIcon = (k: SortKey) => (sort !== k ? "↕" : dir === "asc" ? "↑" : "↓");

  return (
    <div className="card">
      <div className="row" style={{ alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0 }}>Watchlist</h2>
        <button onClick={load} disabled={refreshing}>
          {refreshing ? "refreshing…" : "refresh"}
        </button>
        <label style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6 }}>
          <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} />
          auto-refresh (30s)
        </label>
      </div>

      {error && <div className="card bad" style={{ marginTop: 8 }}>{error}</div>}
      {!rows && !error && <div className="card" style={{ marginTop: 8 }}>Loading…</div>}

      {rows && (
        <div style={{ overflowX: "auto", marginTop: 8 }}>
          <table className="wl-table" style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <Th onClick={() => onSort("symbol")} title="Symbol">
                  Symbol <small>{sortIcon("symbol")}</small>
                </Th>
                <Th onClick={() => onSort("last")} title="Last Price">
                  Last Price <small>{sortIcon("last")}</small>
                </Th>
                <Th onClick={() => onSort("change_abs")} title="Change $">
                  Change $ <small>{sortIcon("change_abs")}</small>
                </Th>
                <Th onClick={() => onSort("change_pct")} title="Change %">
                  Change % <small>{sortIcon("change_pct")}</small>
                </Th>
                <Th onClick={() => onSort("bid")} title="Bid">
                  Bid <small>{sortIcon("bid")}</small>
                </Th>
                <Th onClick={() => onSort("ask")} title="Ask">
                  Ask <small>{sortIcon("ask")}</small>
                </Th>
                <Th title="Today’s range with marker at last">Day’s Range</Th>
                <Th title="52-week range with marker at last">52 Week Range</Th>
                <Th onClick={() => onSort("day_volume")} title="Volume" style={{ textAlign: "right" as const }}>
                  Volume <small>{sortIcon("day_volume")}</small>
                </Th>
                <Th onClick={() => onSort("last_ts")} title="Last trade time (ET)" style={{ textAlign: "right" as const }}>
                  Last Trade (ET) <small>{sortIcon("last_ts")}</small>
                </Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const change$ =
                  r.change_abs ?? (r.prev_close != null && r.last != null ? r.last - r.prev_close : null);
                const changePct =
                  r.change_pct ?? (r.prev_close ? ((r.last! - r.prev_close) / r.prev_close) * 100 : null);

                const cls =
                  change$ == null ? "" : change$ > 0 ? "pos" : change$ < 0 ? "neg" : "";

                return (
                  <tr key={r.symbol}>
                    <Td mono>{r.symbol}</Td>
                    <Td mono>{fmtPx4(r.last)}</Td>
                    <Td mono className={cls}>{change$ == null ? "—" : (change$ >= 0 ? "+" : "") + fmtPx4(Math.abs(change$))}</Td>
                    <Td mono className={cls}>{changePct == null ? "—" : (changePct >= 0 ? "+" : "") + fmtPct(Math.abs(changePct))}</Td>
                    <Td mono>{fmtPx4(r.bid)}</Td>
                    <Td mono>{fmtPx4(r.ask)}</Td>
                    <Td>
                      <RangeBar
                        low={r.day_low}
                        high={r.day_high}
                        val={r.last}
                        title={`Day: ${fmtPx2(r.day_low)} — ${fmtPx2(r.day_high)} • last ${fmtPx2(r.last)}`}
                      />
                    </Td>
                    <Td>
                      <RangeBar
                        low={r.w52_low}
                        high={r.w52_high}
                        val={r.last}
                        title={`52w: ${fmtPx2(r.w52_low)} — ${fmtPx2(r.w52_high)} • last ${fmtPx2(r.last)}`}
                      />
                    </Td>
                    <Td mono style={{ textAlign: "right" }}>{fmtInt(r.day_volume)}</Td>
                    <Td mono style={{ textAlign: "right" }}>{fmtET(r.last_ts)}</Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* minimal styles tuned for your dark theme */}
      <style>{`
        .wl-table th, .wl-table td {
          padding: 10px 12px;
          border-bottom: 1px solid var(--border, #1f2a36);
          vertical-align: middle;
          white-space: nowrap;
        }
        .wl-table thead th {
          position: sticky; top: 0;
          background: var(--card, #0f1720);
          z-index: 1;
          font-weight: 600;
          text-align: left;
          cursor: pointer;
          user-select: none;
        }
        .wl-table thead th:last-child { text-align: right; }
        .wl-table tbody td:nth-last-child(2),
        .wl-table thead th:nth-last-child(2) { text-align: right; }
        .wl-table tbody td:last-child,
        .wl-table thead th:last-child { text-align: right; }
        .pos { color: var(--good, #22c55e); }
        .neg { color: var(--bad, #ef4444); }
        .mono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace; }
        @media (max-width: 1100px) {
          /* hide 52w on narrow screens to preserve layout */
          .wl-table th:nth-child(8), .wl-table td:nth-child(8) { display: none; }
        }
      `}</style>
    </div>
  );
}

/** Small header/data cell helpers */
function Th(props: React.HTMLAttributes<HTMLTableCellElement>) {
  return <th {...props} />;
}
function Td({ mono, ...rest }: React.HTMLAttributes<HTMLTableCellElement> & { mono?: boolean }) {
  return <td {...rest} className={(rest.className ?? "") + (mono ? " mono" : "")} />;
}
